import { useState, useEffect, useRef } from "preact/hooks"
import { useLocation } from "wouter-preact"
import { useSearch, navigate } from "wouter-preact/use-location"
import { CalendarListView } from "./promo_calendar/calendar_list_view"
import { TRADE_PROMOTION_MANAGEMENT_PATH } from "./routes"
import { PromoCreation } from "./promo_creation/components/promo_creation"
import { EnhancedPromotionEditModal } from "./promo_calendar/components/enhanced_promotion_edit_modal"
import { TradePromotionChatPanel } from "./promo_calendar/components/TradePromotionChatPanel"
import { any_promotions_loading, usePromotionalContextAPI } from "./promo_creation/hooks/use_promotional_context_api"
import { Promotion } from "./promo_creation/types"
import { Button } from "../../components/ui/button"
import { toast } from "../../components/ui/use-toast"
import { UserState } from "../../auth/user"
import { generatePromotionEffectivenessSeedData } from "../../api/promotion_effectiveness"
import { Loader2, Sparkles } from "lucide-react"

export function TradePromotionManagement() {
  const user = UserState.use()
  // API hook with all the business logic
  const {
    calendars,
    promotion_load_by_calendar,
    is_loading_calendars,
    calendars_load_error,
    isProvidingFeedback,
    isAddingPromotion,
    fetchCalendars,
    hydrate_calendars_for_year,
    hydrate_all_calendars,
    handleProvideFeedback,
    handleCreatePromotion,
    handleCreatePromotionWithFiles,
    handleUpdatePromotion,
    handleUpdateCalendar,
    isUpdatingCalendar,
    handleUpdatePromotionStage,
    handleDeletePromotion,
    handleDuplicatePromotion,
    handleCopyForward,
    handleSplitFixedFee,
    getPromotion,
  } = usePromotionalContextAPI()

    // UI state
  const [editingPromotion, setEditingPromotion] = useState<any>(null)
  const [chatOpen, setChatOpen] = useState(false)
  const [creatingRetailCalendar, setCreatingRetailCalendar] = useState(false)
  const [location] = useLocation()
  const search = useSearch()
  const deep_link_loaded_ref = useRef<string | null>(null)

  // Load calendars on mount
  useEffect(() => {
    fetchCalendars()
  }, [])

  // Deep-link support: open the promotion popup when `?promotion=<id>` is present in the URL.
  useEffect(() => {
    const promo_id = new URLSearchParams(search).get("promotion")
    if (!promo_id) {
      deep_link_loaded_ref.current = null
      return
    }
    if (editingPromotion?.id === promo_id || deep_link_loaded_ref.current === promo_id) return

    deep_link_loaded_ref.current = promo_id
    let cancelled = false
    void getPromotion(promo_id).then((promotion) => {
      if (!cancelled && promotion) setEditingPromotion(promotion)
    })
    return () => {
      cancelled = true
    }
  }, [search, editingPromotion?.id])

  function openPromotion(promotion: any) {
    setEditingPromotion(promotion)
    if (promotion?.id) {
      const params = new URLSearchParams(search)
      params.set("promotion", promotion.id)
      const query = params.toString()
      navigate(`${location || TRADE_PROMOTION_MANAGEMENT_PATH}${query ? `?${query}` : ""}`, { replace: true })
      // Some callers (the Accrual view) pass only a row's few fields. The edit form saves every
      // field, so a stub left in place showed Planned Amount blank and saved the blanks over the
      // promotion. Load the whole promotion, as the `?promotion=` deep link does.
      const promotion_id: string = promotion.id
      void getPromotion(promotion_id).then((full) => {
        if (full) setEditingPromotion((current: Promotion | null) => (current?.id === promotion_id ? full : current))
      })
    }
  }

  function closePromotion() {
    setEditingPromotion(null)
    const params = new URLSearchParams(search)
    if (params.get("promotion")) {
      params.delete("promotion")
      const query = params.toString()
      navigate(`${location || TRADE_PROMOTION_MANAGEMENT_PATH}${query ? `?${query}` : ""}`, { replace: true })
    }
  }

  async function handleCreateRetailCalendar() {
    setCreatingRetailCalendar(true)
    try {
      const result = await generatePromotionEffectivenessSeedData()
      toast({
        title: "TPM calendar created",
        description: `${result.promotion_count} promotions, ${result.metric_rows} metric rows, and ${result.deduction_count} matched deductions created.`,
      })
      await fetchCalendars()
    } catch (error) {
      toast({
        title: "TPM calendar creation failed",
        description: error instanceof Error ? error.message : "Could not create TPM data.",
        variant: "destructive",
      })
    } finally {
      setCreatingRetailCalendar(false)
    }
  }

  // Handle escape key to close edit modal
  useEffect(() => {
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape' && editingPromotion) {
        closePromotion()
      }
    }

    if (editingPromotion) {
      document.addEventListener('keydown', handleEscapeKey)
      return () => document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [editingPromotion])

  // Enhanced handlers that manage UI state
  async function onProvideFeedback(calendar: any, feedback: string): Promise<boolean> {
    const success = await handleProvideFeedback(calendar, feedback)
    return success || false
  }

  async function onCreatePromotion(calendarId: string, promotion: any): Promise<boolean> {
    const success = await handleCreatePromotion(calendarId, promotion)
    return success || false
  }

  async function onEditPromotion(promotion: any) {
    openPromotion(promotion)
  }

  function onCalendarCreated(calendarId: string) {
    // Simply refresh calendars - the CalendarListView will auto-detect and open the new one
    fetchCalendars()
  }

  return (
    <div class="w-full px-4">
      {/* Calendar List View */}
      <CalendarListView
        onOpenTradeWiz={() => setChatOpen(true)}
        calendars={calendars}
        promotion_load_by_calendar={promotion_load_by_calendar}
        is_loading_calendars={is_loading_calendars}
        calendars_load_error={calendars_load_error}
        onEditPromotion={onEditPromotion}
        onUpdatePromotion={handleUpdatePromotion}
        onDeletePromotion={handleDeletePromotion}
        onCreatePromotion={onCreatePromotion}
        onUpdateCalendar={handleUpdateCalendar}
        isUpdatingCalendar={isUpdatingCalendar}
        onUpdatePromotionStage={handleUpdatePromotionStage}
        onDuplicatePromotion={handleDuplicatePromotion}
        onCopyForward={handleCopyForward}
        onSplitFixedFee={handleSplitFixedFee}
        onProvideFeedback={onProvideFeedback}
        isProvidingFeedback={isProvidingFeedback}
        isAddingPromotion={isAddingPromotion}
        hydrateCalendarsForYear={hydrate_calendars_for_year}
        hydrateAllCalendars={hydrate_all_calendars}
      />
      
      {/* Divider */}
      <div class="border-t border-gray-200 my-8"></div>
      
      {/* Promotional Context and Creation */}
      <PromoCreation
        calendars={calendars}
        onCalendarCreated={onCalendarCreated}
        onCreatePromotion={handleCreatePromotionWithFiles}
        onCopyForward={handleCopyForward}
        onPromotionsCopied={(copied) => {
          // Open the first copied promotion right away instead of making the user search for it.
          if (copied[0]) openPromotion(copied[0])
        }}
        onEnsureAllPromotionsLoaded={hydrate_all_calendars}
        promotionsLoading={any_promotions_loading(is_loading_calendars, promotion_load_by_calendar)}
      />

      {user?.role === "system_admin" ? (
        <div class="mt-10 border-t border-gray-200 pt-4">
          <div class="flex flex-wrap items-center justify-end gap-3">
            <div class="text-right text-xs text-gray-500">
              Create a full TPM calendar with matched deductions and syndicated metrics. Existing calendars are not changed.
            </div>
            <Button onClick={handleCreateRetailCalendar} disabled={creatingRetailCalendar} variant="outline" size="sm">
              {creatingRetailCalendar ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Create TPM calendar
            </Button>
          </div>
        </div>
      ) : null}
      
      {/* Enhanced Promotion Edit Modal */}
      <EnhancedPromotionEditModal
        promotion={editingPromotion}
        isOpen={!!editingPromotion}
        onClose={closePromotion}
        onSave={async (updatedPromotion: Promotion) => {
          await handleUpdatePromotion(updatedPromotion)
          // Keep the modal open and refresh the in-modal promotion so edits (e.g. planned amount)
          // are reflected immediately on the Insights tab.
          setEditingPromotion({ ...updatedPromotion })
        }}
        onDelete={async (promotionId: string) => {
          await handleDeletePromotion(promotionId)
          closePromotion()
        }}
        onCopyForwardComplete={(created: Promotion) => {
          // Refresh the calendars and jump straight to the newly created copy.
          fetchCalendars()
          openPromotion(created)
        }}
      />
      <TradePromotionChatPanel open={chatOpen} onClose={() => setChatOpen(false)} />
    </div>
  )
}
